/**
 * Format a value in pence as £-prefixed pounds with two decimal places and
 * a thousands separator. Negative values get a leading minus sign before the £.
 *
 * formatPence(123456) === "£1,234.56"
 * formatPence(0)      === "£0.00"
 * formatPence(-500)   === "-£5.00"
 */
export function formatPence(pence: number): string {
  if (!Number.isFinite(pence)) return "£0.00";
  const negative = pence < 0;
  const abs = Math.abs(Math.round(pence));
  const pounds = (abs / 100).toFixed(2);
  const [whole = "0", frac = "00"] = pounds.split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}£${withCommas}.${frac}`;
}

/**
 * Validate and pass through a sort code in the format "12-34-56".
 * Strips any whitespace; throws on invalid input.
 */
export function formatSortCode(input: string): string {
  const trimmed = input.replace(/\s+/g, "");
  if (!/^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(trimmed)) {
    throw new Error(`invalid sort code: ${input}`);
  }
  return trimmed;
}

/**
 * Render a day-of-month with the correct ordinal suffix.
 *
 * formatRentDueDay(1)  === "1st"
 * formatRentDueDay(2)  === "2nd"
 * formatRentDueDay(11) === "11th"
 * formatRentDueDay(21) === "21st"
 */
export function formatRentDueDay(day: number): string {
  if (!Number.isInteger(day) || day < 1 || day > 31) {
    throw new Error(`invalid rent due day: ${day}`);
  }
  const lastTwo = day % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/**
 * Format an ISO date string ("YYYY-MM-DD") as a UK-style date "12 Apr 2026".
 * Returns "—" for null/empty input.
 */
export function formatDateGB(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "—";
  const [, year, month, day] = m;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const idx = Number.parseInt(month ?? "0", 10) - 1;
  const monthName = months[idx];
  if (!monthName) return "—";
  return `${Number.parseInt(day ?? "0", 10)} ${monthName} ${year}`;
}

/**
 * Compute the next rent-due date in ISO form given a day-of-month.
 * If today's day-of-month is past `dueDay`, returns next month; else this month.
 * Uses UTC to keep tests deterministic.
 */
export function nextRentDueDateUTC(dueDay: number, now: Date = new Date()): string {
  if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31) {
    throw new Error(`invalid due day: ${dueDay}`);
  }
  const yr = now.getUTCFullYear();
  const mo = now.getUTCMonth();
  const today = now.getUTCDate();
  let candidateY = yr;
  let candidateM = today <= dueDay ? mo : mo + 1;
  if (candidateM > 11) {
    candidateM = 0;
    candidateY += 1;
  }
  const lastDay = new Date(Date.UTC(candidateY, candidateM + 1, 0)).getUTCDate();
  const day = Math.min(dueDay, lastDay);
  const mm = String(candidateM + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${candidateY}-${mm}-${dd}`;
}
